/* eslint-disable max-lines -- static notification config is inherently long */
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags";

import { FacilityRoles, FacilityUserPermissions } from "../types";
import { ALERT_TYPE, type WorkplaceNotificationsMap } from "./types";

export const CHARGE_RATE_VISIBLE_EMAIL = "CHARGE_RATE_VISIBLE_EMAIL";
const WORKER_ETA_SMS = "WORKER_ETA_SMS";

export function getDefaultWorkplaceNotifications(roles?: string[]): WorkplaceNotificationsMap {
  return {
    EMAIL: {
      INV_EMAIL: {
        mandatoryRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
        alertTypes: [
          {
            default: {
              enabled: true,
              batch: false,
              userModifiableBatch: false,
              userModifiableState: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Invoice Email",
            action: "invoiceEmail",
            role: "INV_EMAIL",
            mandatoryRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
            mandatoryPermissions: [FacilityUserPermissions.MANAGE_INVOICES],
            validRoles: [FacilityRoles.INVOICES, FacilityRoles.ADMIN],
            validPermissions: [FacilityUserPermissions.MANAGE_INVOICES],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      SFT_EMAIL: {
        alertTypes: [
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Shift Assignment",
            action: "assignmentEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.POST_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Shift Unassign",
            action: "shiftUnassignmentEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.CANCEL_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Workplace Staffing Summary",
            action: "staffingSummary",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              userModifiableBatch: true,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Rate Negotiation",
            action: "rateNegotiationEmail",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worked Break Payment Request",
            action: "ewtrEmails",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.INVOICES],
            validPermissions: [FacilityUserPermissions.REVIEW_EXTRA_TIME_PAYMENT_REQUESTS],
          },
          {
            disableBatching: true,
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worker ETA",
            action: "workerEtaEmail",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.INVOICES,
              FacilityRoles.SHIFT_MANAGEMENT,
            ],
          },
          {
            default: {
              enabled: false,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Chat messages",
            action: "chatMessages",
            role: "SFT_EMAIL",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.RECRUITMENT,
            ],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_EMAIL",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Worker Left Early Replacement",
            action: "wleReplacementEmail",
            role: "SFT_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.SHIFT_MANAGEMENT],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      CHARGE_RATE_VISIBLE_EMAIL: {
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Charge Rates Visibility",
            action: "chargeRateVisibility",
            role: CHARGE_RATE_VISIBLE_EMAIL,
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      CDPH530_DAILY_EMAIL: {
        lddFlag: CbhFeatureFlag.CALIFORNIA_TIMESHEET,
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Daily CDPH-530 Recap",
            action: "dailyCDPH530Recap",
            role: "CDPH530_DAILY_EMAIL",
            validRoles: [FacilityRoles.ADMIN, FacilityRoles.SHIFT_MANAGEMENT],
          },
        ],
        type: ALERT_TYPE.EMAIL,
      },
      REC_EMAIL: {
        lddFlag: CbhFeatureFlag.PLACEMENTS_ACCESS_CONTROL_ROLLOUT,
        type: ALERT_TYPE.EMAIL,
        alertTypes: [
          {
            default: {
              enabled: false,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Candidate Apply",
            action: "candidateApplyEmail",
            role: "REC_EMAIL",
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.RECRUITMENT,
            ],
          },
          {
            default: {
              enabled: true,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Interview Attendance Confirmed",
            action: "interviewAttendanceConfirmed",
            role: "REC_EMAIL",
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.RECRUITMENT,
            ],
          },
          {
            default: {
              enabled: roles ? roles.includes(FacilityRoles.RECRUITMENT) : false,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Job Schedule Updates",
            action: "jobScheduleUpdatesEmail",
            role: "REC_EMAIL",
            validRoles: Object.values(FacilityRoles),
          },
          {
            default: {
              enabled: false,
              userModifiableBatch: false,
              userModifiableState: true,
              batch: false,
              time: 16,
            },
            type: ALERT_TYPE.EMAIL,
            title: "Send upcoming interview updates",
            action: "upcomingInterviewUpdatesEmail",
            role: "REC_EMAIL",
            validRoles: Object.values(FacilityRoles),
          },
        ],
      },
    },
    SMS: {
      SFT_SMS: {
        alertTypes: [
          {
            default: {
              enabled: true,
              batch: false,
              notifyRole: "SFT_SMS",
              userModifiableBatch: true,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Shift Assignment SMS",
            action: "assignmentSMS",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.POST_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: true,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Shift Unassign SMS",
            action: "shiftUnassignmentSMS",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
            ],
            validPermissions: [FacilityUserPermissions.CANCEL_SHIFT_PERMISSION],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: false,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Signature Request (By Us)",
            action: "shiftCBHSignatureRequestSMS",
            role: "SFT_SMS",
            validRoles: [FacilityRoles.SHIFT_MANAGEMENT, FacilityRoles.ADMIN],
          },
          {
            default: {
              enabled: true,
              notifyRole: "SFT_SMS",
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Chat messages",
            action: "chatMessages",
            role: "SFT_SMS",
            validRoles: [
              FacilityRoles.SHIFT_MANAGEMENT,
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.RECRUITMENT,
            ],
          },
        ],
        type: ALERT_TYPE.SMS,
      },
      REC_SMS: {
        lddFlag: CbhFeatureFlag.PLACEMENTS_ACCESS_CONTROL_ROLLOUT,
        alertTypes: [
          {
            default: {
              enabled: roles ? roles.includes(FacilityRoles.RECRUITMENT) : false,
              batch: false,
              notifyRole: "REC_SMS",
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Job Schedule Updates",
            action: "jobScheduleUpdatesSMS",
            role: "REC_SMS",
            validRoles: Object.values(FacilityRoles),
          },
          {
            default: {
              enabled: false,
              batch: false,
              notifyRole: "REC_SMS",
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Send upcoming interview updates",
            action: "upcomingInterviewUpdatesSMS",
            role: "REC_SMS",
            validRoles: Object.values(FacilityRoles),
          },
          {
            default: {
              enabled: false,
              batch: false,
              notifyRole: "REC_SMS",
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Interview worker ETA",
            action: "interviewWorkerETASMS",
            role: "REC_SMS",
            validRoles: Object.values(FacilityRoles),
          },
        ],
        type: ALERT_TYPE.SMS,
      },
      WORKER_ETA_SMS: {
        alertTypes: [
          {
            disableBatching: true,
            default: {
              enabled: true,
              notifyRole: WORKER_ETA_SMS,
              batch: false,
              userModifiableBatch: false,
              userModifiableState: true,
              time: 16,
            },
            type: ALERT_TYPE.SMS,
            title: "Worker ETA",
            action: "workerEtaSMS",
            role: WORKER_ETA_SMS,
            validRoles: [
              FacilityRoles.ADMIN,
              FacilityRoles.DOCUMENTS,
              FacilityRoles.INVOICES,
              FacilityRoles.SHIFT_MANAGEMENT,
            ],
          },
        ],
        type: ALERT_TYPE.SMS,
      },
    },
  };
}
/* eslint-enable max-lines */
